import type { UseMutationResult } from '@tanstack/react-query'
import { BoolIcon } from '@/components/common/BoolIcon'
import { useRegionsAdmin, useCreateRegion, useUpdateRegion, useDeleteRegion } from '@/features/region/hooks'
import type { Region } from '@/features/region/types'
import type { DictionaryPanelConfig } from '../DictionaryPanel'

const APPEND_SORT_ORDER = 1_000_000

export const regionsConfig: DictionaryPanelConfig<Region> = {
  title: 'Regiony',
  description: 'Oddziały firmy (region biznesowy).',
  addLabel: 'Dodaj region',
  useList: useRegionsAdmin,
  useCreate: () => {
    const m = useCreateRegion()
    return {
      ...m,
      mutate: (payload: Record<string, unknown>, opts?: Parameters<typeof m.mutate>[1]) =>
        m.mutate(
          {
            code: String(payload['code'] ?? ''),
            labelPl: String(payload['labelPl'] ?? ''),
            sortOrder: APPEND_SORT_ORDER,
            isActive: Boolean(payload['isActive'] ?? true),
          },
          opts,
        ),
    } as unknown as UseMutationResult<Region, Error, Record<string, unknown>>
  },
  useUpdate: () => {
    const m = useUpdateRegion()
    return {
      ...m,
      mutate: (
        vars: { code: string; patch: Record<string, unknown> },
        opts?: Parameters<typeof m.mutate>[1],
      ) =>
        m.mutate(
          {
            code: vars.code,
            payload: {
              labelPl: String(vars.patch['labelPl'] ?? ''),
              sortOrder: Number(vars.patch['sortOrder'] ?? APPEND_SORT_ORDER),
              isActive: Boolean(vars.patch['isActive'] ?? true),
            },
          },
          opts,
        ),
    } as unknown as UseMutationResult<Region, Error, { code: string; patch: Record<string, unknown> }>
  },
  useDeactivate: useDeleteRegion,
  columns: [
    { key: 'code', header: 'Kod', className: 'font-mono text-slate-500 w-40' },
    { key: 'labelPl', header: 'Etykieta' },
    { key: 'isActive', header: 'Aktywne', render: (r) => BoolIcon({ value: r.isActive }) },
  ],
  formFields: [
    { key: 'code', label: 'Kod', type: 'code', required: true },
    { key: 'labelPl', label: 'Etykieta', type: 'text', required: true },
    { key: 'isActive', label: 'Aktywne', type: 'boolean' },
  ],
}
